// SchemaMarkup.js - Schema component using React 19 built-in features
import { useEffect } from 'react';

const SchemaMarkup = ({ locationData }) => {
  useEffect(() => {
    if (!locationData) return;

    // Helper function to format opening hours for schema
    const formatOpeningHours = (hours) => {
      const dayMapping = {
        monday: 'Mo',
        tuesday: 'Tu', 
        wednesday: 'We',
        thursday: 'Th',
        friday: 'Fr',
        saturday: 'Sa',
        sunday: 'Su'
      };

      const openingHours = [];
      
      Object.entries(hours).forEach(([day, dayHours]) => {
        if (!dayHours.closed && dayHours.open && dayHours.close) {
          openingHours.push(`${dayMapping[day]} ${dayHours.open}-${dayHours.close}`);
        }
      });

      return openingHours;
    };

    // Restaurant/Local Business Schema
    const restaurantSchema = {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "name": locationData.name,
      "image": locationData.photoSrc ? [`${window.location.origin}/images/locations/${locationData.photoSrc}`] : [],
      "description": `Kinder's Meats Deli BBQ & Catering location in ${locationData.address.city}, ${locationData.address.state}. Offering pickup, delivery, and catering services.`,
      "url": window.location.href,
      "telephone": locationData.phoneNumber,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": `${locationData.address.address1}${locationData.address.address2 ? ' ' + locationData.address.address2 : ''}`,
        "addressLocality": locationData.address.city,
        "addressRegion": locationData.address.state,
        "postalCode": locationData.address.zip,
        "addressCountry": "US"
      },
      ...(locationData.coordinates && {
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": locationData.coordinates.lat,
          "longitude": locationData.coordinates.lng
        }
      }),
      "openingHours": formatOpeningHours(locationData.hours),
      "servesCuisine": ["BBQ", "American", "Deli"],
      "priceRange": "$$",
      "acceptsReservations": false,
      "takeaway": true,
      "delivery": true,
      "hasMenu": true,
      "parentOrganization": {
        "@type": "Organization",
        "name": "Kinder's Meats Deli BBQ & Catering",
        "url": "https://kindersmeats.com"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": locationData.phoneNumber,
        "contactType": "customer service"
      },
      ...(locationData.rating && {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": locationData.rating.average,
          "reviewCount": locationData.rating.count,
          "bestRating": "5",
          "worstRating": "1"
        }
      }),
      "paymentAccepted": ["Cash", "Credit Card", "Debit Card"],
      "currenciesAccepted": "USD"
    };

    // Organization Schema for the parent company
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Kinder's Meats Deli BBQ & Catering",
      "url": "https://kindersmeats.com",
      "logo": "https://kindersmeats.com/images/logo.png", // Update with actual logo URL
      "description": "Family-owned BBQ restaurant chain serving authentic barbecue, deli meats, and catering services across California.",
      "foundingDate": "1946", // Update with actual founding date
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": locationData.phoneNumber,
        "contactType": "customer service",
        "availableLanguage": "English"
      },
      "sameAs": [
        "https://www.facebook.com/KindersMeats", // Update with actual social media URLs
        "https://www.instagram.com/kindersmeats",
        "https://www.yelp.com/biz/kinders-meats"
      ]
    };

    // Breadcrumb Schema
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://kindersmeats.com"
        },
        {
          "@type": "ListItem", 
          "position": 2,
          "name": "Locations",
          "item": "https://kindersmeats.com/locations"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": locationData.name,
          "item": window.location.href
        }
      ]
    };

    // Function to add/update schema script tags
    const addSchemaScript = (schema, id) => {
      // Remove existing script if it exists
      const existingScript = document.getElementById(id);
      if (existingScript) {
        existingScript.remove();
      }

      // Create new script tag
      const script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema, null, 2);
      document.head.appendChild(script);
    };

    // Add all schemas
    addSchemaScript(restaurantSchema, 'restaurant-schema');
    addSchemaScript(organizationSchema, 'organization-schema');
    addSchemaScript(breadcrumbSchema, 'breadcrumb-schema');

    // Cleanup function to remove scripts when component unmounts
    return () => {
      const schemaIds = ['restaurant-schema', 'organization-schema', 'breadcrumb-schema'];
      schemaIds.forEach(id => {
        const script = document.getElementById(id);
        if (script) {
          script.remove();
        }
      });
    };
  }, [locationData]);

  // This component doesn't render anything visible
  return null;
};

export default SchemaMarkup;